/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Button } from "@mui/material";
import React, { type JSX } from "react";
import { createPortal } from "react-dom";
import {
  CloseButtonProps,
  Id,
  Slide,
  toast,
  ToastContainer as ToastifyContainer,
  ToastContent,
  ToastOptions,
} from "react-toastify";

import TestIds from "../testIds";

import "./useToast.scss";

// Above MUI modal (1300) / snackbar (1400). Portaled to body so stacking isn't
// trapped under Dialog portals (e.g. activities modal).
const TOAST_Z_INDEX = 99999;

export const ToastContainer = (): JSX.Element => {
  const closeButton = ({ closeToast }: CloseButtonProps): JSX.Element => (
    <Box className="fl-use-toast__toast__close">
      <Button
        variant="farmlands2.0_material-icon"
        color="secondary"
        onClick={closeToast}
        data-testid={TestIds.components.toastify.closeBtn}
      >
        close
      </Button>
    </Box>
  );

  return createPortal(
    <ToastifyContainer
      transition={Slide}
      position="bottom-center"
      limit={1}
      autoClose={2000}
      newestOnTop
      hideProgressBar
      closeOnClick
      closeButton={closeButton}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="dark"
      className="fl-use-toast__toast-container"
      toastClassName="fl-use-toast__toast"
      style={{ zIndex: TOAST_Z_INDEX }}
    />,
    document.body
  );
};

export const useToast = () => {
  const toastId = React.useRef<Id | undefined>(undefined);

  const notify = (content: ToastContent<unknown>, options?: ToastOptions): void => {
    dismissAll();
    toastId.current = toast(content, {
      className: "fl-use-toast__toast",
      ...options,
    });
  };

  const dismiss = () => toast.dismiss(toastId.current);

  const dismissAll = () => toast.dismiss();

  return {
    notify,
    dismiss,
    dismissAll,
  };
};

export default useToast;
